import { useTranslation } from 'react-i18next';
import type { OpticalJobStatusChangeSummary } from '@optiflow/types';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime } from '@/utils/format';

/**
 * What happened to a job and when, oldest first.
 *
 * Statuses, times and who moved it — nothing else: no prescription value, no payment and no internal detail ever sits in a status change. Rows a LEGACY job carries from earlier records say so.
 */
export function JobTimeline({ history }: { history: readonly OpticalJobStatusChangeSummary[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  if (history.length === 0) {
    return <p className="text-muted text-sm">{t('opticalOrders.tracking.noHistory')}</p>;
  }

  return (
    <ol className="space-y-3" aria-label={t('opticalOrders.tracking.timeline')}>
      {history.map((row, index) => {
        const legacy = row.kind === 'LEGACY_BACKFILL' || row.kind === 'LEGACY_WORKFLOW';
        return (
          <li key={`${row.changedAt}-${index}`} className="flex gap-3">
            <span aria-hidden className="bg-brand-500 mt-1.5 size-2 shrink-0 rounded-full" />
            <div className="min-w-0 text-sm">
              <p className="text-ink font-medium">
                {t(`sales.opticalJobs.status.${row.toStatus}`)}
                {legacy ? (
                  <span className="text-muted ms-2 text-xs font-normal">
                    {t('opticalOrders.tracking.fromEarlierRecords')}
                  </span>
                ) : null}
              </p>
              <p className="text-muted text-xs">
                <time dateTime={row.changedAt}>{formatDateTime(row.changedAt, locale)}</time>
                {row.changedByName
                  ? ` · ${t('opticalOrders.tracking.by', { name: row.changedByName })}`
                  : ''}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
